"use client";

import React from "react";
import dynamic from "next/dynamic";
import { GlobalEntityDetailModal } from "./GlobalEntityDetailModal";
import { GlobalQuickActionModals } from "./GlobalQuickActionModals";
import { StatusChangeModal } from "./DetailPageModals/StatusChangeModal";
import { SnoozeDealModal } from "./DetailPageModals/SnoozeDealModal";
import { SetFollowUpDateModal } from "./DetailPageModals/SetFollowUpDateModal";
import { RequestQuoteModal } from "./DetailPageModals/RequestQuoteModal";
import { MarkDealWonModal } from "./DetailPageModals/MarkDealWonModal";
import { MarkDealLostModal } from "./DetailPageModals/MarkDealLostModal";
import { ChangeDealStageModal } from "./DetailPageModals/ChangeDealStageModal";
import { MakeProjectChannelModal } from "./DetailPageModals/MakeProjectChannelModal";
import { ChargeCardOnFileModal } from "@/features/recurring-charges/components/ChargeCardOnFileModal";
import { ChangeScheduleModal } from "@/features/recurring-charges/components/ChangeScheduleModal";
import { TaskAddNoteModalContainer } from "@/features/tasks/components/TaskAddNoteModal";
import { AddTicketUpdateModalContainer } from "@/features/tickets/components/AddTicketUpdateModal";
import { ReassignCompanyModal } from "./DetailPageModals/ReassignCompanyModal";
import { ChangeCompanyDriveFolderModal } from "./DetailPageModals/ChangeCompanyDriveFolderModal";
import { WithdrawContractModal } from "./DetailPageModals/WithdrawContractModal";
import { ActionResolverModal } from "@/components/shared/ActionResolver";
import { MODAL_IDS } from "@wtc/ui";
import { Suspense } from "react";

// These modals are mounted globally on every route but render null until their
// entry is opened via the modal stack. Loading their code with next/dynamic
// keeps it out of the shared client bundle shipped on first paint — the chunk
// is fetched lazily after hydration and the closed render is still null, so
// there is no behavior change. Only the heavier / rarely-opened ones are split;
// the small context-free confirm modals above stay in the main chunk.
const CreateDealInvoiceModal = dynamic(
  () => import("./DetailPageModals/CreateDealInvoiceModal").then((m) => m.CreateDealInvoiceModal),
  { ssr: false }
);
const CreateDealQuoteModal = dynamic(
  () => import("./DetailPageModals/CreateDealQuoteModal").then((m) => m.CreateDealQuoteModal),
  { ssr: false }
);
const SendTicketCustomerEmailModal = dynamic(
  () =>
    import("./DetailPageModals/SendTicketCustomerEmailModal").then(
      (m) => m.SendTicketCustomerEmailModal
    ),
  { ssr: false }
);
const CreateMeetingModalContainer = dynamic(
  () =>
    import("./DetailPageModals/CreateMeetingModalContainer").then(
      (m) => m.CreateMeetingModalContainer
    ),
  { ssr: false }
);
const EditContractModal = dynamic(
  () => import("./DetailPageModals/EditContractModal").then((m) => m.EditContractModal),
  { ssr: false }
);
const PublishSocialPostModal = dynamic(
  () => import("./DetailPageModals/PublishSocialPostModal").then((m) => m.PublishSocialPostModal),
  { ssr: false }
);
const EntityAuditModal = dynamic(
  () => import("@/components/audit/EntityAuditModal").then((m) => m.EntityAuditModal),
  { ssr: false }
);
const UserDetailModal = dynamic(
  () =>
    import("@/components/admin/users/UserDetailModal/UserDetailModal").then(
      (m) => m.UserDetailModal
    ),
  { ssr: false }
);

/**
 * Renders all global modals available on every page
 *
 * This component renders:
 * 1. Quick action modals (compose, create task, create contact, etc.)
 * 2. Entity detail modals (when opened from other pages via URL)
 *
 * These modals are available everywhere via the global modal stack.
 */
function DetailPageModalsComponent() {
  return (
    <>
      {/* Quick action modals - available from the quick actions dropdown */}
      <GlobalQuickActionModals />

      {/* Contact/Company detail modal when opened from other pages (e.g. address map) via URL */}
      <Suspense fallback={null}>
        <GlobalEntityDetailModal />
      </Suspense>

      {/* Entity status modal (supports URL modal param and row actions) */}
      <StatusChangeModal />

      {/* Deal action modals (opened from deal row/detail actions via onOpenModal).
          Context-free (data-payload pattern) so they are safe to mount globally. */}
      <SnoozeDealModal />
      <SetFollowUpDateModal />
      <RequestQuoteModal />
      <CreateDealInvoiceModal />
      <CreateDealQuoteModal />
      <MarkDealWonModal />
      <MarkDealLostModal />
      <ChangeDealStageModal />
      <MakeProjectChannelModal />

      {/* Contract (e-signature) destructive confirm modal — Withdraw / Void.
          Context-free (data-payload pattern); POSTs to /api/contracts/[id]/withdraw. */}
      <WithdrawContractModal />

      {/* Contract (e-signature) edit modal — Hub-side CRM linkage + document name +
          notes. Context-free (data-payload pattern); GET/PATCH /api/contracts/[id]. */}
      <EditContractModal />

      {/* Social post irreversible publish confirm modal — "Post now".
          Context-free (data-payload pattern); POSTs to /api/marketing/social/publish. */}
      <PublishSocialPostModal />

      {/* Recurring-charge action modals (context-free, data-payload). */}
      <ChargeCardOnFileModal />
      <ChangeScheduleModal />

      <TaskAddNoteModalContainer />

      {/* Ticket "Add update" modal (opened by the add-update action from ticket
          rows/detail). Writes a ticket_updates row. Context-free (data-payload). */}
      <AddTicketUpdateModalContainer />

      {/* Ticket customer email (acknowledge receipt / resolution confirmation),
          opened from ticket row/detail actions. Context-free (data-payload);
          POSTs to /api/tickets/[id]/customer-email itself. */}
      <SendTicketCustomerEmailModal />

      {/* Reassign a ticket's company/contact (opened from ticket OR time-entry row
          actions via onOpenModal). Context-free (data-payload pattern). */}
      <ReassignCompanyModal />

      {/* Pick the Google Drive folder backing a company (opened from the
          google_drive_folder_id inline-edit cell / detail action). Context-free
          (data-payload pattern); saves via the codegen-typed company update hook. */}
      <ChangeCompanyDriveFolderModal />

      {/* Schedule-meeting modal (opened by the schedule-meeting action from
          contacts/companies/etc.). Lazily provides CalendarContext so it can
          create real calendar events. Context-free (data-payload pattern). */}
      <CreateMeetingModalContainer />

      {/* Admin user details modal (opened from the admin users table row click
          via openModal(MODAL_IDS.users.detail, { userId })). Reads userId from
          the modal-stack data payload — no DetailActionContext. */}
      <UserDetailModal />

      {/* Cross-entity action resolver — the entry modal for every manifest-based
          action (send-email, log-activity, call/SMS, research, decision-makers).
          Mounted globally (NOT just in EntityPageModals) so the action system
          also works on custom pages that reuse the contact/entity row actions —
          e.g. /sequences, /today. Context-free: reads actionId/sourceEntity/
          sourceId from the modal-stack data payload and renders null when closed. */}
      <ActionResolverModal />

      {/* Entity "History & Audit" (append-only S3 audit log). Places, not
          moments: also registered in MODAL_URL_CONFIG, so each owns a browser
          history entry (Back closes, Forward re-opens). Mounted globally
          because the action that opens them is on every contact/company row,
          card, and detail surface. Context-free (data-payload pattern). */}
      <EntityAuditModal entityType="contacts" modalId={MODAL_IDS.audit.contactsHistory} />
      <EntityAuditModal entityType="companies" modalId={MODAL_IDS.audit.companiesHistory} />
    </>
  );
}

// ============================================================================
// MEMOIZED EXPORT
// ============================================================================

/**
 * Memoize DetailPageModals to prevent re-renders from parent layout changes.
 * This component has no props, so it's always equal.
 */
export const DetailPageModals = React.memo(DetailPageModalsComponent);

